/**
 * page-actions.js — the ONE page-actions menu (BC-9, F8).
 *
 * Page actions lived in three places with three shapes: the desktop tab strip
 * hid rename behind double-click/F2, duplicate and delete behind a right-click
 * menu, and reorder behind drag or Alt+Arrow; the collapsed page list offered
 * only "switch" and "New page"; the phone had a proper actions sheet. The
 * command palette's Rename and Reorder drove the desktop strip even when it was
 * hidden. Every surface now renders THIS model — same items, same reasons,
 * same re-resolution at click — and only the container differs.
 *
 * Handlers re-read the store when they run. A menu can outlive the state that
 * opened it (the page was deleted, the order changed, the tab went read-only),
 * and a stale capture is how "Move up" moves the wrong page.
 */

import { store, readOnlyReasonFor } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { icon } from './icons.js';
import { openPopoverMenu } from './popover-menu.js';
import { movePageId } from './command-bar/page-order.js';

/**
 * @typedef {{id: string, label: string, glyph: string, reason: string, danger?: boolean, run: () => void}} PageAction
 */

/**
 * @param {string} pageId
 * @param {{orientation?: 'horizontal'|'vertical', onRename?: (pageId: string) => void}} [opts]
 *   `horizontal` for the tab strip (Move left/right), `vertical` for lists.
 * @returns {{page: object, index: number, actions: PageAction[]}|null}
 */
export function buildPageActions(pageId, { orientation = 'vertical', onRename } = {}) {
  const state = store.getState();
  const pages = Array.isArray(state?.pages) ? state.pages : [];
  const index = pages.findIndex((page) => page.id === pageId);
  if (index < 0) return null;
  const page = pages[index];
  const projectId = state.projectId ?? null;
  const readOnly = store.isWritable() ? '' : readOnlyReasonFor(store.getPrimaryBlocker());
  const horizontal = orientation === 'horizontal';

  /** The page as it is NOW, or null when it (or its project) is gone. */
  const live = () => {
    const now = store.getState();
    if ((now?.projectId ?? null) !== projectId) return null;
    return (now?.pages || []).find((candidate) => candidate.id === pageId) || null;
  };
  const move = (direction) => () => {
    if (!live()) return;
    const orderedIds = movePageId(
      store.getState().pages.map((candidate) => candidate.id),
      pageId,
      direction
    );
    if (orderedIds) eventBus.emit('toolbar:reorder-pages', { orderedIds });
  };

  const actions = [
    {
      id: 'rename',
      label: 'Rename…',
      glyph: 'rename',
      reason: readOnly,
      run: () => {
        if (live() && typeof onRename === 'function') onRename(pageId);
      }
    },
    {
      id: 'move-back',
      label: horizontal ? 'Move left' : 'Move up',
      glyph: horizontal ? 'chevronLeft' : 'chevronUp',
      reason: readOnly || (index === 0 ? 'This is the first page.' : ''),
      run: move(-1)
    },
    {
      id: 'move-forward',
      label: horizontal ? 'Move right' : 'Move down',
      glyph: horizontal ? 'chevronRight' : 'chevronDown',
      reason: readOnly || (index === pages.length - 1 ? 'This is the last page.' : ''),
      run: move(1)
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      glyph: 'duplicate',
      reason: readOnly,
      run: () => {
        if (live()) eventBus.emit('toolbar:duplicate-page', { pageId });
      }
    },
    {
      id: 'delete',
      label: 'Delete',
      glyph: 'trash',
      danger: true,
      reason: readOnly || (pages.length === 1 ? 'Keep at least one page.' : ''),
      run: () => {
        // Re-checked at commit: the last-page rule may have become true since.
        if (live() && store.getState().pages.length > 1) {
          eventBus.emit('toolbar:delete-page', { pageId });
        }
      }
    }
  ];
  return { page, index, actions };
}

/**
 * Open the page-actions menu for `pageId`: a popover beside `anchorEl` (or at
 * `anchorRect`) on desktop, a sheet on a phone. Disabled items SHOW their
 * reason — a phone user never sees a tooltip.
 */
export function openPageActions(
  pageId,
  { anchorEl = null, anchorRect = null, orientation = 'horizontal', onRename, restoreFocus } = {}
) {
  const model = buildPageActions(pageId, { orientation, onRename });
  if (!model) return null;
  return openPopoverMenu(anchorEl, {
    owner: 'page-actions',
    align: 'left',
    width: 240,
    anchorRect: anchorRect || undefined,
    menuLabel: `Page actions: ${model.page.name}`,
    phoneSheet: 'auto',
    restoreFocus,
    items: model.actions.map((action) => ({
      id: action.id,
      label: action.label,
      icon: icon(action.glyph),
      danger: Boolean(action.danger),
      disabled: Boolean(action.reason),
      disabledReason: action.reason || undefined,
      showReason: true,
      onSelect: action.run
    }))
  });
}
